import type { MouseEvent } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ErrorBoundary } from './components/ErrorBoundary';

import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Conversations from './pages/Conversations';

/**
 * App shell. The Shopify admin paints this app's primary navigation; the app
 * does not paint its own.
 *
 * `<ui-nav-menu>` is an App Bridge custom element — the CDN script in
 * index.html defines it — that registers its `<a>` children as items in the
 * admin's own sidebar. Once App Bridge upgrades the element it gives itself a
 * shadow root whose only rule is `:host{display:none}`, so it contributes
 * nothing to the page inside the iframe.
 *
 * It replaces a Polaris <Frame> + <Navigation> + <TopBar> that failed three
 * Built for Shopify criteria at once: the app painted a second primary
 * navigation beside the admin's own (4.1.4); the Frame hardcoded
 * `showMobileNavigation={false}` while the bare <TopBar> rendered no navigation
 * toggle, so on a narrow viewport every route became unreachable (4.1.2); and
 * that <TopBar> was a full-width bar with nothing in it (4.1.1).
 *
 * `ui-nav-menu` is typed by @shopify/app-bridge-types, which src/vite-env.d.ts
 * references — the same footing Settings.tsx's <ui-save-bar> stands on. Deleting
 * that reference is a compile error here, which is the point; a hand-written
 * JSX declaration would instead go quietly out of date against the vendor's.
 */
/**
 * Asks the admin to confirm before a navigation throws away unsaved edits, or
 * null when there is nobody to ask.
 *
 * Reached through the DOM and the App Bridge global rather than by importing a
 * helper from pages/Settings. The save bar is App Bridge's own element, so the
 * document is the interface the two sides already share, and a module edge from
 * this shell to a page would be the thing that breaks: the shell's own suite
 * stubs each page with a factory that returns `default` alone, and vitest
 * throws on any other export read through that stub.
 *
 * `showing` is the gate, and it is the whole guard. Settings renders the only
 * `<ui-save-bar>` in this app, and only where `host` is on the URL and App
 * Bridge is loaded — so an element that reports itself showing has already
 * established every fact a separate embeddedness check would establish. Where
 * the element is absent, or present but never upgraded by App Bridge (the
 * component tests, the .shots screenshot rig), `showing` is undefined and this
 * returns null, leaving navigation synchronous exactly as it was.
 *
 * The promise is handed back untouched. It resolves when there is nothing
 * unsaved or the merchant chose to leave — and App Bridge hides the bar itself
 * before it resolves — so a caller navigates on fulfilment only.
 */
function confirmLeavingUnsavedEdits(): Promise<void> | null {
  const bar = document.querySelector('ui-save-bar') as UISaveBarElement | null;
  if (!bar?.showing) return null;

  // Re-typed as optional before it is read, for the reason
  // config/shopify-app-bridge.ts memorialises: App Bridge declares this global
  // as a `var` that always exists, and every member of the vendor's save bar
  // API is optional, so the vendor's own types would compile a call that
  // throws wherever the CDN script did not run.
  const api = (
    window as unknown as {
      shopify?: { saveBar?: Partial<typeof window.shopify.saveBar> };
    }
  ).shopify?.saveBar;
  if (typeof api?.leaveConfirmation !== 'function') return null;

  // `Promise.resolve(...)` for the reason Settings.tsx wraps its own bar calls:
  // this is somebody else's runtime, and `.then` on a non-promise is a
  // TypeError thrown out of a click handler.
  return Promise.resolve(api.leaveConfirmation());
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Routes the click inside the SPA instead of letting App Bridge hand it to
   * the admin.
   *
   * When the merchant picks one of these items in the admin sidebar, App Bridge
   * clicks the matching anchor and then checks whether that click was
   * prevented. Both of its navigation paths do this — the
   * `Navigation.redirect.app` handler on the element itself, and the separate
   * `navigate` listener used when the host reports `navigationVersion === 2` —
   * and in both, prevented means "the app routed this itself". Left
   * unprevented, App Bridge cancels the browser default itself and asks the
   * HOST to navigate, which reloads this document: the React Query cache and
   * all page state go with it on every nav click.
   *
   * Those two paths differ in one further respect, and it is why nothing routes
   * here until the admin's save bar has been asked. On a host reporting
   * `navigationVersion === 2`, App Bridge also installs a capture-phase click
   * listener that swallows the event outright while a save bar is showing — so
   * this handler is never reached and the admin does the asking. On an older
   * host nothing checks: `Navigation.redirect.app` synthesises the click, this
   * handler runs, and a sidebar click over a dirty Settings form routes away
   * with the merchant's typing. That is the case being closed, and it is the
   * case the vendor documents `leaveConfirmation` for ("custom routing that is
   * not anchor tag" — preventing this default to hand the click to the router
   * is exactly that). The two paths are alternatives, never both at once, so
   * there is no double prompt to worry about.
   *
   * `search` is carried across for the reason the old Polaris nav carried it. A
   * plain <a href> would leave this document's URL with no `host` param, and
   * main.tsx boots App Bridge only when `host` is present — so the next reload
   * of that URL would come up with no bridge to the admin at all.
   */
  const routeInApp =
    (pathname: string) => (event: MouseEvent<HTMLAnchorElement>) => {
      // First and synchronous, always: App Bridge reads `defaultPrevented` at
      // the window as soon as the click it synthesised returns, so deferring
      // this behind the confirmation would hand the navigation to the host and
      // reload the document.
      event.preventDefault();
      const go = () => navigate({ pathname, search: location.search });

      const leaving = confirmLeavingUnsavedEdits();
      if (!leaving) {
        go();
        return;
      }
      leaving.then(go, (reason) => {
        // A rejection is ambiguous and the ambiguity is not ours to settle: the
        // admin rejects when the merchant answers "stay", and the same call
        // rejects if the bridge handshake fails. Staying put is right for the
        // first and the only safe answer to the second — but a merchant whose
        // sidebar click silently does nothing deserves a trace, so a rejection
        // carrying an Error is logged while a bare "stay" stays quiet. Passed
        // as `then`'s second argument rather than a trailing `.catch`, which
        // would also swallow anything `go` itself threw.
        if (reason instanceof Error) {
          console.warn(
            '[brilio] Leave confirmation did not resolve; staying on this page.',
            reason,
          );
        }
      });
    };

  return (
    <>
      {/* Bare paths on the hrefs, never location.search: App Bridge registers
          each anchor's resolved href with the admin as that nav item's URL, and
          this app's query string carries `id_token`. A session token has no
          business in the admin's navigation. */}
      <ui-nav-menu>
        <a href="/" rel="home" onClick={routeInApp('/')}>
          Dashboard
        </a>
        <a href="/conversations" onClick={routeInApp('/conversations')}>
          Conversations
        </a>
        <a href="/settings" onClick={routeInApp('/settings')}>
          Settings
        </a>
      </ui-nav-menu>

      {/* The Frame used to render this document's only `main` landmark, so
          dropping it would leave a screen reader inside the app frame with no
          content landmark to jump to. No skip link goes with it: there is no
          in-app navigation left to skip past. */}
      <main>
        {/* Route-level, and a SIBLING of the nav menu rather than a child of
            it. A page that throws must not unmount <ui-nav-menu> — App Bridge
            drops the admin's items when that element disconnects, and the
            merchant is left on the broken route with nothing to click (#2752).
            The Frame used to be what guaranteed that; this placement is what
            guarantees it now. Keyed on the path so navigating away clears the
            error. */}
        <ErrorBoundary resetKey={location.pathname} withPage>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/conversations" element={<Conversations />} />
            <Route path="/settings" element={<Settings />} />
            <Route
              path="*"
              element={<Navigate to={{ pathname: '/', search: location.search }} replace />}
            />
          </Routes>
        </ErrorBoundary>
      </main>
    </>
  );
}
